import { type FocusEventHandler, memo } from 'react';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import type { SvgIconComponent } from '@mui/icons-material';
import type { Row, Table, TableOptions, VisibilityState } from '@tanstack/react-table';
import { TableBody as MuiTableBody } from '@mui/material';
import { TableRow } from './TableRow';
import type { DataSchema, HeliosRowControls, HeliosRowFunctionsControls, Schema } from 'types';
import type { TABLE_CELL_TYPE } from '../../../types/enums';
import type { UseFormReturn } from 'react-hook-form';
import type { TableFormData } from './Table';
import { getSelectRowId } from '../../../utils/table';
import type { RowControls } from 'types/table';
import type { TableColumnAggregationState } from 'utils/table.ts';
import type { VisibilityResult } from 'types/records.ts';

interface Props<T extends keyof P, P extends DataSchema> {
  selectAllForm: UseFormReturn<TableFormData<boolean>>;
  agendaId?: string;
  tableInstance: Table<Schema<T, P>>;
  selectedRows: string[];
  handleRowSelect: (identifier: string, isChecked: boolean) => void;
  data: Schema<T, P>[];

  dataSchema: P;
  tableIndex: number;

  cellClicked?: (row: TableOptions<Schema<T, P>>['data'], cell: keyof P) => void;
  entireRowClicked?: (row: TableOptions<Schema<T, P>>['data'][number]) => void;
  showRowClickStyles?: boolean;
  editRowId?: string[] | null;
  onSaveEditedRow?: (
    row: {
      [key in keyof P]: {
        value: string | number | boolean | object;
        type: TABLE_CELL_TYPE;
      };
    } extends {
      id: {
        value: string;
        type: TABLE_CELL_TYPE.ID;
      };
    }
      ? {
          [key in keyof P]: {
            value: string | number | boolean | object;
            type: TABLE_CELL_TYPE;
          };
        }
      : {
          [key in keyof P]: {
            value: string | number | boolean | object;
            type: TABLE_CELL_TYPE;
          };
        } & {
          id: {
            value: string;
            type: TABLE_CELL_TYPE.ID;
          };
        }
  ) => void;
  innerTable?: (row: TableOptions<Schema<T, P>>['data'][number], tableIndex: number) => React.ReactNode;
  tableControls?: {
    [key: string]: {
      visible: boolean;
      disabled?: boolean;
      onClick: () => void;
      icon: SvgIconComponent;
      label: string;
      id: string;
    };
  };

  rowControls?: RowControls<TableOptions<Schema<T, P>>['data'][number]>;
  heliosRowControls?: HeliosRowControls<TableOptions<Schema<T, P>>['data'][number]>;
  heliosRowFunctionsControls?: HeliosRowFunctionsControls[];

  onBlurCapture?: FocusEventHandler<HTMLTableSectionElement>;
  clickableCellsKeys: (keyof P)[] | 'all';
  hideCheckbox?: boolean;
  onCancelEditRow?: (id: string) => void;
  isHeliosTable?: boolean;
  columnVisibility?: VisibilityState;
  columnAggregation?: TableColumnAggregationState;
  visibilityResults?: Record<string, VisibilityResult>;
  dataTestIdPrefix?: string;
  testIdPrefix?: string;
  visibleRowCount?: number;
}

export const TableBodyBase = <T extends keyof P, P extends DataSchema>({
  selectAllForm,
  agendaId,
  tableInstance,
  selectedRows,
  handleRowSelect,
  cellClicked,
  entireRowClicked,
  showRowClickStyles = true,
  editRowId,
  dataSchema,
  innerTable,
  tableIndex,
  rowControls,
  heliosRowControls,
  heliosRowFunctionsControls,
  tableControls,
  clickableCellsKeys,
  hideCheckbox,
  onSaveEditedRow,
  onCancelEditRow,
  isHeliosTable,
  onBlurCapture,
  columnVisibility,
  columnAggregation,
  visibilityResults,
  dataTestIdPrefix,
  testIdPrefix,
  visibleRowCount,
}: Props<T, P>) => {
  const resolvedTestIdPrefix = dataTestIdPrefix ?? testIdPrefix;

  const handleCellClick = (row: TableOptions<Schema<T, P>>['data'], cell: keyof P) => {
    if (cellClicked) {
      cellClicked(row, cell);
    }
  };

  const isRowSelected = (row: Row<Schema<T, P>>) => {
    const selectedId = getSelectRowId(row.original, Boolean(isHeliosTable));
    return selectedRows.includes(selectedId);
  };

  const allRows = tableInstance.getRowModel().rows;
  const visibleRows = visibleRowCount ? allRows.slice(0, visibleRowCount) : allRows;

  return (
    <MuiTableBody onBlurCapture={onBlurCapture}>
      <SortableContext items={visibleRows.map((row) => row.original.identifier)} strategy={verticalListSortingStrategy}>
        {visibleRows.map((row, index) => (
          <TableRow<T, P>
            testIdPrefix={resolvedTestIdPrefix}
            selectAllForm={selectAllForm}
            agendaId={agendaId}
            row={row}
            numberOfVisibleRows={tableInstance.getRowModel().rows.length}
            selectedRow={isRowSelected(row)}
            handleRowSelect={handleRowSelect}
            key={`${row.original.identifier}-${index}`}
            cellClicked={handleCellClick}
            entireRowClicked={entireRowClicked}
            showRowClickStyles={showRowClickStyles}
            editRowId={editRowId}
            dataSchema={dataSchema}
            innerTable={innerTable}
            tableIndex={tableIndex}
            rowControls={rowControls}
            heliosRowControls={heliosRowControls}
            heliosRowFunctionsControls={heliosRowFunctionsControls}
            tableControls={tableControls}
            clickableCellsKeys={clickableCellsKeys}
            hideCheckbox={hideCheckbox}
            onSaveEditedRow={onSaveEditedRow}
            onCancelEditRow={onCancelEditRow}
            isHeliosTable={isHeliosTable}
            columnVisibility={columnVisibility}
            columnAggregation={columnAggregation}
            visibilityResults={visibilityResults}
          />
        ))}
      </SortableContext>
    </MuiTableBody>
  );
};

export const TableBody = memo(TableBodyBase) as typeof TableBodyBase;
